import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface ScrollState {
  scrollTop: number;
  timestamp: number;
}

const SCROLL_STORAGE_KEY = 'training-samples-scroll';
const SCROLL_TIMEOUT = 5 * 60 * 1000; // 5 minutes

export const useScrollPersistence = () => {
  const location = useLocation();
  const hasRestoredScroll = useRef(false);

  const saveScrollPosition = () => {
    const scrollState: ScrollState = {
      scrollTop: window.scrollY,
      timestamp: Date.now()
    };
    sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(scrollState));
  };

  const restoreScrollPosition = () => {
    if (hasRestoredScroll.current) return;
    
    const stored = sessionStorage.getItem(SCROLL_STORAGE_KEY);
    if (!stored) return;

    try {
      const scrollState: ScrollState = JSON.parse(stored);
      const isExpired = Date.now() - scrollState.timestamp > SCROLL_TIMEOUT;
      
      if (!isExpired) {
        const maxScrollTop = document.documentElement.scrollHeight - window.innerHeight;
        const targetScrollTop = Math.min(scrollState.scrollTop, maxScrollTop);
        
        if (targetScrollTop > 0) {
          window.scrollTo(0, targetScrollTop);
          hasRestoredScroll.current = true;
          
          // If we couldn't reach the full scroll position (content not loaded yet),
          // don't mark as restored so we can try again
          if (targetScrollTop < scrollState.scrollTop && maxScrollTop < scrollState.scrollTop) {
            hasRestoredScroll.current = false;
          }
        }
      } else {
        sessionStorage.removeItem(SCROLL_STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error restoring scroll position:', error);
      sessionStorage.removeItem(SCROLL_STORAGE_KEY);
    }
  };

  const clearScrollPosition = () => {
    sessionStorage.removeItem(SCROLL_STORAGE_KEY);
    hasRestoredScroll.current = false;
  };

  useEffect(() => {
    const isReturningFromDetail = location.state?.fromDetail;
    
    if (isReturningFromDetail) {
      // Restore scroll position when returning from detail view
      // Use a longer delay to allow content to load first
      setTimeout(() => {
        restoreScrollPosition();
        // If content is still loading, try again in a bit
        setTimeout(() => {
          restoreScrollPosition();
        }, 500);
      }, 200);
    } else {
      // Clear saved position if we're not returning from detail
      clearScrollPosition();
    }

    const handleBeforeUnload = () => {
      saveScrollPosition();
    };

    const handleScroll = () => {
      // Always save scroll position to capture it before navigation
      saveScrollPosition();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.state]);

  // Save scroll position when component unmounts (like when navigating to detail)
  useEffect(() => {
    return () => {
      saveScrollPosition();
    };
  }, []);

  return {
    saveScrollPosition,
    restoreScrollPosition,
    clearScrollPosition
  };
};